import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Info, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Persistent banner shown at the top of the authenticated app when the
 * signed-in user is the shared demo account (`is_demo === true`).
 *
 * Purpose:
 *   1. Set expectations for contest judges — this is a sandbox estate,
 *      any changes reset on next demo login.
 *   2. Drive conversion — a curious prospect who came in via the demo
 *      button gets a one-click path to /register.
 *
 * Dismiss state is intentionally scoped to sessionStorage: judges get
 * ONE quiet dismissal per session, but a fresh browser tab / new login
 * shows it again. That's what we want — the banner is deliberately
 * hard-to-miss for anyone new to the demo.
 */

const STORAGE_KEY = 'epf.demo_banner_dismissed';

const DemoModeBanner = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  if (!user?.is_demo || dismissed) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="bg-gradient-to-r from-emerald-700 via-emerald-800 to-emerald-900 text-white border-b border-emerald-950"
      role="status"
      data-testid="demo-mode-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/15">
          <Info className="w-3.5 h-3.5" />
        </span>
        <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-4">
          <p className="text-sm leading-snug">
            <span className="font-semibold">You're exploring the live demo.</span>{' '}
            <span className="text-white/80">
              This estate is sandbox data — any edits reset on the next demo login. Ready to manage a real one?
            </span>
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 mt-2 sm:mt-0 text-sm font-medium bg-white text-emerald-800 px-3 py-1.5 rounded-md hover:bg-white/95 flex-shrink-0"
            data-testid="demo-banner-signup-btn"
          >
            Sign up to save your own data
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss demo banner"
          className="flex-shrink-0 text-white/70 hover:text-white p-1"
          data-testid="demo-banner-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default DemoModeBanner;
